import { CommentsCount } from "@/components/comments/CommentsCount.tsx";
import { DataBars, DataBarsRow } from "@/components/data-bars/DataBars";
import { FormAggregatedResultElementProps } from "@/components/form-result/aggregated-result/components/types.ts";
import {
  Box,
  ButtonIcon,
  FontAwesomeIcon,
  Stack,
  Text,
  Title,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@skailer-com/uikit";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export const FormAggregatedResultSelectElement = ({
  element,
  result,
  showDetailModalCallback,
}: FormAggregatedResultElementProps) => {
  const selectSettings = element.select_settings;
  const { t } = useTranslation();
  const [sortByPopularity, setSortByPopularity] = useState(false);

  const rows = useMemo(() => {
    if (!result || !selectSettings || !result.select_data) return null;

    let sum = 0;
    for (const option of selectSettings.options) {
      const selectOption = result.select_data.options.find(
        (x) => x.selectable_option_id === option.id
      );
      sum += selectOption?.count || 0;
    }

    const rowsInner: DataBarsRow[] = [];
    for (const option of selectSettings.options) {
      const selectOption = result.select_data.options.find(
        (x) => x.selectable_option_id === option.id
      );
      const count = selectOption?.count || 0;
      const percent = sum > 0 ? Math.round((100 * count) / sum) : 0;
      rowsInner.push({
        title: option.title,
        emoji: option.emoji,
        value: count > 0 ? `${percent}%` : t("common:no_votes"),
        percent: percent,
        tooltip: (
          <Stack className={"text-sm"}>
            {count > 0 ? (
              <Text>{t("plurals:responses_count", { count: count })}</Text>
            ) : (
              <Text>{t("common:no_votes")}</Text>
            )}
            <Text className={"text-sm text-light/60"}>{t("common:click_to_view_answers")}</Text>
          </Stack>
        ),
        onClick: showDetailModalCallback
          ? () =>
              showDetailModalCallback(element, {
                answer_select_option_ids: [option.id],
              })
          : undefined,
      });
    }

    if (sortByPopularity) {
      rowsInner.sort((a, b) => b.percent - a.percent);
    }

    return rowsInner;
  }, [result, sortByPopularity]);

  if (!selectSettings) return null;
  return (
    <Box
      className={`group/box ${showDetailModalCallback ? "cursor-pointer" : ""}`}
      onClick={showDetailModalCallback ? () => showDetailModalCallback(element) : undefined}
    >
      <Stack direction={"horizontal"} gap={"md"} items={"center"} justify={"between"}>
        <Title
          header={selectSettings.title || t("surveys:question_type_select")}
          size={"sm"}
          className={selectSettings.title ? "" : "text-secondary"}
          required={selectSettings.is_required}
        />
        <Tooltip>
          <TooltipTrigger>
            <ButtonIcon
              className={"group/btn opacity-0 group-hover/box:opacity-100 transition-opacity"}
              icon={
                <Stack direction={"horizontal"}>
                  <FontAwesomeIcon icon="fa-light fa-arrow-down-arrow-up hidden group-hover/btn:block" />
                  {sortByPopularity ? (
                    <FontAwesomeIcon icon="fa-light fa-arrow-down-9-1 group-hover/btn:hidden" />
                  ) : (
                    <FontAwesomeIcon icon="fa-light fa-list-ol group-hover/btn:hidden" />
                  )}
                </Stack>
              }
              variant={"secondary"}
              onClick={(e) => {
                e.stopPropagation();
                setSortByPopularity(!sortByPopularity);
              }}
            />
          </TooltipTrigger>
          <TooltipContent>
            {sortByPopularity ? t("common:sort_by_original_order") : t("common:sort_by_popularity")}
          </TooltipContent>
        </Tooltip>
      </Stack>

      {result && result.select_data && result.select_data.comments_count > 0 && (
        <Box className={"py-4"}>
          <CommentsCount
            totalComments={result.select_data.comments_count}
            commentersPreview={result.select_data.commenters_preview || []}
          />
        </Box>
      )}
      {rows && <DataBars rows={rows} />}
    </Box>
  );
};
